import React from 'react';
import { Box, Container, Typography, Grid } from '@mui/material';
import TitlePage from '../components/TitlePage';
import StatsSection from '../components/StatsSection';
import CustomButton from '../components/common/CustomButton';
import headerImage from '../assets/images/about/6.jpg';
import { styled } from '@mui/material/styles';
import SecurityIcon from '@mui/icons-material/Security';
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';
import HandshakeIcon from '@mui/icons-material/Handshake';
import CTASection from '../components/CTASection';

const ValueCard = styled(Box)(({ theme }) => ({
  padding: '2.5rem',
  background: '#FFFFFF',
  borderRadius: '16px',
  height: '100%',
  border: '1px solid #E2E8F0',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-4px)',
    borderColor: '#1A365D'
  }
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  width: '72px',
  height: '72px',
  borderRadius: '20px',
  background: 'linear-gradient(135deg, #1A365D 0%, #004B87 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: '2rem',
  '& svg': {
    fontSize: '36px',
    color: '#FFFFFF',
  }
}));

const values = [
  {
    icon: <SecurityIcon />,
    title: 'Confidentialité et Impartialité',
    description: 'Nous nous engageons à protéger les informations sensibles de nos clients tout en garantissant une totale impartialité dans nos analyses. Nous veillons à ce que chaque résultat soit objectif et indépendant, sans influence externe, pour assurer une fiabilité totale des données fournies.'
  },
  {
    icon: <PrecisionManufacturingIcon />,
    title: 'Rigueur et Expertise',
    description: 'Nos laboratoires s\'appuient sur une expertise technique pointue et une rigueur scientifique inébranlable pour garantir des résultats d\'une précision optimale. Chaque analyse est effectuée selon les normes les plus strictes afin d\'offrir des solutions conformes aux attentes de nos clients.'
  },
  {
    icon: <HandshakeIcon />,
    title: 'Responsabilité et engagement',
    description: 'Nous assumons pleinement notre responsabilité envers nos clients et la société en respectant des standards élevés de qualité et de sécurité. Notre engagement se traduit par des actions concrètes en matière d\'amélioration continue, afin de contribuer à la durabilité et à l\'excellence de nos services.'
  }
];

const WhoWeAre = () => {
  return (
    <Box>
      <TitlePage 
        title="Qui sommes-nous ?" 
        image={headerImage}
      />
      
      <Container 
        maxWidth="xl"
        sx={{
          py: { xs: 6, md: 10 },
          px: { xs: 2, sm: 4, md: 8, lg: 12 }
        }}
      >
        <Typography 
          variant="h2" 
          sx={{ 
            color: '#1A365D',
            fontSize: { xs: '2rem', md: '2.5rem' },
            fontWeight: 700,
            mb: 4
          }}
        >
          À propos du Groupe
        </Typography>

        <Grid container spacing={4}>
          <Grid item xs={12}>
            <Typography   
              sx={{ 
                color: '#475569',
                fontSize: '1.1rem',
                lineHeight: 1.7,
                mb: 3
              }}
            >
              En tant que spécialiste des services de métrologie, notre raison d'être est de contribuer à l'optimisation de la performance industrielle de nos différents clients par l'amélioration de leurs processus de mesure.
            </Typography>
            <Typography 
              sx={{ 
                color: '#475569',
                fontSize: '1.1rem',
                lineHeight: 1.7,
                mb: 3
              }}
            >
              Nous faisons partie des leaders à garantir une offre multiservice d'étalonnage et de vérification des instruments de mesure en toute indépendance. Avec plus de 20 accréditations, notre groupe se positionne au sommet des laboratoires à l'échelle nationale en matière de diversification des domaines, couvrant une superficie de 2100 m2 et employant aujourd'hui plus de 60 personnes dans 16 laboratoires principaux qui assurent une couverture homogène de l'ensemble des besoins nationaux.
            </Typography>

            <Typography 
              sx={{ 
                color: '#475569',
                fontSize: '1.1rem',
                lineHeight: 1.7,
                mb: 3
              }}
            >
              Au-delà des prestations techniques, nous apportons à nos clients notre expertise dans le déploiement de solutions personnalisées pour la gestion de parc métrologique.
            </Typography>

            <Typography 
              sx={{ 
                color: '#475569',
                fontSize: '1.1rem',
                lineHeight: 1.7,
                mb: 6
              }}
            >
              AIR METROLOGIE – L2MI proposent également des formations et des services de support technique dans le domaine de la métrologie. Année après année, nous sommes fiers d'être devenus les partenaires de confiance des leaders dans les secteurs de l'aéronautique, de l'automobile, du pharmaceutique, de l'agroalimentaire, de l'industrie lourde et du secteur pétrolier, etc.
            </Typography>
          </Grid>
        </Grid>
      </Container>

      <StatsSection />

      <Container 
        maxWidth="xl"
        sx={{
          py: { xs: 6, md: 10 },
          px: { xs: 2, sm: 4, md: 8, lg: 12 }
        }}
      >
        <Typography 
          variant="h2" 
          sx={{ 
            color: '#1A365D',
            fontSize: { xs: '2rem', md: '2.5rem' },
            fontWeight: 700,
            mb: 2,
            textAlign: 'center'
          }}
        >
          Nos valeurs
        </Typography>

        <Typography 
          sx={{ 
            color: '#475569',
            fontSize: '1.2rem',
            textAlign: 'center',
            mb: 6
          }}
        >
          sont représentées dans chacune de nos actions
        </Typography>

        <Grid container spacing={4} sx={{ mb: 8 }}>
          {values.map((value, index) => (
            <Grid item xs={12} md={4} key={index}>
              <ValueCard>
                <IconWrapper>
                  {value.icon}
                </IconWrapper>
                <Typography 
                  variant="h3" 
                  sx={{ 
                    color: '#1A365D',
                    fontSize: '1.5rem',
                    fontWeight: 600,
                    mb: 2
                  }}
                >
                  {value.title}
                </Typography>
                <Typography 
                  sx={{ 
                    color: '#475569',
                    fontSize: '1rem',
                    lineHeight: 1.7
                  }}
                >
                  {value.description}
                </Typography>
              </ValueCard>
            </Grid>
          ))}
        </Grid>

        <Box 
          sx={{ 
            display: 'flex',
            justifyContent: 'center',
            gap: 3,
            flexWrap: 'wrap'
          }}
        >
          <CustomButton 
            variant="contained"
            href="/politique-qualite-airmetrologie"
          >
            Politique qualité Air métrologie
          </CustomButton>
          <CustomButton 
            variant="contained"
            href="/politique-qualite-l2mi"
          >
            Politique qualité L2MI
          </CustomButton>
        </Box>
      </Container>
      <CTASection />
    </Box>
  );
};

export default WhoWeAre;
